/**
 * 卡面积分标的**单一真值源**(E0 纠偏,2026-07-23)。
 *
 * ★为什么有这个文件:卡墙 `costLabel` 与工作台 `DEPT_ENTRIES.costLabel` 原本各写各的
 * 硬编码数字(「42 积分/张」「120 积分/片」「96 积分/条」「解析 18 积分」),**与平台真实
 * 费率差 16~69 倍,而且一律往少了说**。同一个 app 内已有反证:
 *   - `BlueprintDetailSheet` 显示后端下发的真实 `bp.parseCost`(实测 346),卡上却写「解析 18」;
 *   - `useVideoCreditEstimate` 注释明写「用 capability 带回的真实费率,**不用前端假单价**」,
 *     而卡面写的正是前端假单价。
 * 用户按卡上的数字决定做不做,扣费时才发现差一到两个数量级 —— 这是对客红线,
 * 不是文案瑕疵。故收成本文件一处,并由单测锁死。
 *
 * ★真值来源(全部实测,非推导):
 *   1. 平台费率 = 后端下发:`GET /video/provider-capabilities` 的 `creditsPerSecond` /
 *      `frameImageCredits` / `resolutionMultipliers`;`GET /poster/provider-capabilities`
 *      的 `defaultResolutionTier`。
 *   2. 派生公式(ai `llm_client._credits_from_cost`):积分 = ⌈成本micro × 10 ÷ 10000⌉
 *      = ⌈micro/1000⌉,成本单价在 `xb_model_catalog.input_price_micro`。
 *   3. 2026-07-23 测试机实测账单交叉验证(`xb_billing_event`):
 *      liveportrait 8 秒 → 160 积分(=20/秒);wan2.2-s2v 5 秒 → 2500、14 秒 → 7000(=500/秒);
 *      cosyvoice-v3-flash-audio 8 秒 → 80(=10/秒);gpt-image-2 tier=2K ×12 张 → 8400(=700/张);
 *      小红书 audio_only 解析 → parse_cost 346。
 *
 * ★口径:标签一律用**计费单位**(每秒 / 每张)表达,不编「每片/每条」总价 —— 总价取决于
 * 用户在页内选的时长 / 张数 / 档位,写死一个总数必然又变成假单价。需要精确总价的地方
 * 页内已有真估算器(视频页 `useVideoCreditEstimate`)。
 *
 * ★改价时:平台调了 `xb_model_catalog` 就要同步本文件 + `creditRates.test.ts`。
 * 卡片按设计不做运行时询价(Plan §3.1),所以这是**人工闸**。
 *
 * ⚠️★2026-08-25 更正这里原来的一句话。原文写的是「单测会因数字对不上而红」——
 *   **那是假的,而且是最危险的那种假**:本文件的单测钉的是「常量 vs 写死的期望值」,
 *   两边都在前端。`xb_model_catalog` 改价时**没有任何一条会红** —— 它只在有人手动
 *   改了常量、却没同步期望值时才红。⇒ 对"平台改价"这件事,这套单测**结构上失明**。
 *   2026-08-23 17:21 gpt-5.5 单价 in×3 / out×9,全量门一路绿到底,
 *   直到用户报「卡面 300 实扣 1446」才被发现(见下面 EXPLOSIVE 那条)。
 *   ⇒ 真正的闸只能是**拿真单账对**(定期跑 `xb_billing_event` 分布比对区间),
 *   不是这里的任何一条 expect。别再照那句话去相信它。
 */

/* ═══════════════════════════════════════════════════════════════════════════
 * ★ 2026-08-26 批 L:**单位价常量整批下线**,改从后端价目表现取
 *   (`GET /api/v1/pricing/catalog` → `usePriceBook()` → `PriceBook.unit(sku, variant)`)。
 *
 * 下线的是:GPT_IMAGE_MICRO_1K / IMAGE_TIER_MULTIPLIER / IMAGE_CREDITS_BY_TIER /
 * IMAGE_CREDITS_1K / POSTER_CREDITS_PER_IMAGE / TRYON_CREDITS_PER_IMAGE /
 * VIDEO_CREDITS_PER_SECOND / VIDEO_RESOLUTION_MULTIPLIER / VIDEO_FRAME_CREDITS /
 * DH_CREDITS_PER_SECOND / VIDEO_DEFAULT_CLIP_CREDITS / NORMALIZE_CREDITS_APPROX。
 *
 * ★ 为什么非删不可,而不是「把数字改对」:
 *   这批常量是 ×10 时代写的,而除出图外的全部线路 2026-08 已改成 ×1.6 —— 卡面因此
 *   对着用户**整体高报约 6.25 倍**(换衣 500 实为 80、视频 1000/秒 实为 160、
 *   数字人旗舰 1000/秒 实为 160)。出图那几个之所以还对,正是因为只有它的新旧加价率
 *   都是 ×10。而**没有任何一条判据会红**:本文件的单测钉的是「常量 vs 写死的期望值」,
 *   两边都在前端。这一点本文件头注 2026-08-25 已经承认过一次(「那是假的,而且是最危险
 *   的那种假」)—— 但那次只改了注释,没改结构,于是三个月后同一个病换个数字又来一次。
 *   「把数字改对」是**已知会复发**的选项。
 *
 * ★ 留下来的是**token 计费**那几条(解析 / 成稿 / 雷达)。它们不是「忘了删」:
 *   token 计费**没有单位单价**可言 —— 价目表对 `explosive.parse` 给的是「一个 token
 *   多少分」,印在卡上毫无意义。这几条的真值只能是**实测区间**,而它们的取数依据
 *   (真 `xb_billing_event` 的 settle 分布)就写在各自的注释里。
 *   ⚠️ 它们仍然是人工闸,仍然会随平台改价而失真 —— 见 EXPLOSIVE 那条 0825 的重标记录。
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * 海报创建页**落地默认**分辨率档 —— 用户不显式选档时实际提交的那一档。
 *
 * ★2026-06-15 起创建页「未显式选优先 1K」(`useFreeCreateConstraints.validTier`:
 *   tiers 含 1K 就落 1K,provider `defaultResolutionTier`(4 家都是 2K)只在渠道
 *   不支持 1K 时兜底)。本常量此前一直写 "2K",导致卡面报 2K 价、点进去落 1K 实付
 *   —— 正是 catalog.ts A6 注明令禁止的「卡面报一个档、落地另一个档」(2026-09-08 校准)。
 *
 * ⚠️ 它只描述**海报张卡**的落地默认。长图搭建器默认 scale=2(=2K)、视频首帧实扣 2K、
 *   军师派单默认 2K —— 那些口径各自有显式常量/字面量,别拿这个名字去喂它们。
 */
export const POSTER_DEFAULT_TIER = "1K";

/**
 * 长图**一键出图**的落地默认档 —— 长图用途卡(hub 上墙 4 张 + 隐藏 6 张,`longimage_*`)点进去
 * 默认选中的路径(`CanvasIntakeBox`:带用途 ⇒ 默认「一键出图」)实际提交的那一档。
 *
 * ★0927 K3(第十三批 D2):卡面此前钉死 2K(「默认 8 块 · 3 图块 ≈ 198」= 3×66),而一键路自
 *   0823 批 C-2 起默认 1K(页内「一键出图 · 约 28–308」),0927 演示机实扣 1K 3 屏 84 ——
 *   卡面高报 2.36 倍,与 catalog.ts A6 那条「卡面报一个档、点进去落另一个档」同病。
 * ★单源:`features/poster/model/longImageEstimate.ONECLICK_DEFAULT_TIER` **从这里取值**
 *   (features → entities 是合法方向,反过来 entities 不能 import features),搭建器默认档与
 *   卡面报价档由此是同一个字面量;`sceneCardLongImageParity.test.ts` 再钉一次两者相等,
 *   防止任一侧有人重新写死一个 "2K"。
 * ⚠️ 只描述**一键路**。横宽卡(poster_wide)没有用途、不走一键默认路,落搭建器 scale=2 → 2K,
 *   catalog.ts 另有常量,别拿这个名字去喂它。
 */
export const LONGIMAGE_ONECLICK_DEFAULT_TIER = "1K";

/**
 * 爆款解析实扣区间(取片 + 理解 + 蒸馏三段之和,随片长/档位浮动)。**蓝图复用不再扣**。
 *
 * ★2026-07-28 走查 W3 重校:原值「约 350~450」高于绝大多数真实单据。
 * 依据 = 阳光科技 `xb_billing_event` 里 9 次 `ai_explosive_parse` settle 的真 `deltaUnits`:
 * 72 / 240 / 249 / 288 / 290 / 292 / 293 / 411 / 414(中位 ~290,9 条里只有 2 条 ≥350)。
 * 当天真跑的抖音 53s full 档实扣 240,而蓝图详情页那时写「解析成本 95」——
 * 因为 `parse_cost` 列只记理解段。两处口径都已改:这里给实扣量级,详情页写明「理解段」。
 *
 * ★2026-08-13 追爆专项通宵再校:「约 250~400」两端都不准了。
 * 本轮 14 笔真 settle(租户 T-1B052FB7,四平台混合):
 * 108/117/119/161/181/196/208/217/221/223/230/288/429/551 —— 中位 212、
 * **11/14 低于 250**(报价高于实际,用户以为更贵)、**2/14 高于 400**(报价压不住,
 * 那才是对客红线的方向)。并上 07-28 那 9 笔共 23 笔:中位 230、实测 72~551。
 *
 * 取值口径沿用同文件 `DH_SCRIPT_CREDITS_APPROX` 那条:**上界压住实测最大值**
 * (编一个漂亮的窄区间必然在某一端骗人)。区间宽是这条链路的实情 —— 它是
 * 「取片 + 转写 + 蒸馏」三段 token 计费,成本几乎与**片长**成正比:
 * 本轮 11s 的小红书笔记 117,78s 的那条 551,差 4.7 倍。
 *
 * ★★2026-08-25 重标(BUG-EXPLOSIVE-QUOTE-STALE-1)。用户报「卡面 300 实扣 1446」。
 *
 * **根因不是"单价莫名跳变",是一次正确的改价 + 这个常量没跟着走。**
 * `xb_model_catalog` 里 08-01 之后只有两行动过,都在 **2026-08-23 17:21:02**(0823 校准批):
 *   · gpt-image-2 → 964,800(图价第四版,CEO 拍板,已知)
 *   · **gpt-5.5:input 12,000 → 36,000(3.0×)/ output 24,000 → 216,000(9.0×)**
 * 216,000 micro/千 = 0.216 元/千 ≈ $30/M,正是 GPT-5 级的**真实厂商价**;
 * 改之前的 12,000/24,000 是 V1 baseline 里的**占位价**。⇒ **那次改价是对的。**
 * qwen-plus / deepseek-v4-pro 停在 07-18 没动过。全平台只有三条链路走 gpt-5.5:
 * **视频编排 / 追爆蒸馏 / 出图(08-24 起新增一跳)**;纯 token 计费的
 * advisor·reporter·kb·kefu·geo_radar 改价前后每千 token 成本比 0.80~1.02(没变)——
 * 这是"只打中特定链路"的反证,不是推断。
 *
 * ★真单分布(`xb_billing_event` 全量 settle,按改价时刻切分):
 *   改价前 n=87:69 / 均 279 / 最大 742,破 550 上界 **6 笔 = 7%**
 *   改价后 n=14:177 / 均 653 / 最大 **1469**,破 550 上界 **6 笔 = 43%**
 *   逐单(改价后,降序):1469 1446 1052 993 737 662 486 421 394 360 348 323 279 177
 * 旧的「110~550」是 **08-13 在占位价下**用 23 笔标定的,占位价一换就整体失真。
 *
 * ★新区间 = 改价后实测两端取整(**上界压住 1469**),口径不变。
 * ⚠️ **样本只有 14 笔、只覆盖 08-24~08-25 两天**,比 08-13 那次(23 笔)还薄。
 *    再攒够单子要回来重标 —— 别把它当成已经收敛的值。
 * ⚠️ 这条此前**没有任何守卫**(本文件头注写着「改价时单测会因数字对不上而红」,
 *    而 creditRates.test.ts 里 EXPLOSIVE 命中 0 次 —— 那句话对这条是假的)。
 *    现已补上 `creditRates.test.ts` 的水位锁,并把区间改成数值元组好让判据能真的比大小。
 */
/**
 * ★★2026-09-26 重标(修复波 0926 · 代理 D)—— 口径改成**一单合计**(取片 + 解析),分档。
 *
 * 旧的 [170, 1500] 只算解析那一票、标定于 V33「取片拆票」之前(08-24~25 的 14 笔),
 * 拆票后取片单收 50 分,展示面又写成「取片 X + 解析 170~1500」—— 一个用户按下去只是一次
 * 动作,拆成两段、其中一段还是过期的区间,等于没报价。而且实测早已回落:拆票后的真单
 * 合计全在 147~252 之间,旧上界 1500 高报 6 倍。
 *
 * ★取数(2026-09-26,只读 SELECT;测试机 gscq_test,演示机 gscq_demo 补样):
 *   SELECT b.id, COALESCE(JSON_UNQUOTE(JSON_EXTRACT(b.payload,'$.kind')),'full') kind,
 *          SUM(-e.delta) total
 *   FROM xb_billing_event e JOIN xb_explosive_blueprint b ON b.tenant_code=e.tenant_code
 *     AND e.idempotency_key IN (CONCAT('explosive_parse:',b.id,':settle'),
 *                               CONCAT('explosive_fetch:',b.id,':settle'))
 *   WHERE e.occurred_at >= NOW() - INTERVAL 60 DAY
 *     AND e.sku_code IN ('explosive.fetch','explosive.parse')
 *   GROUP BY b.id;
 * ★只取**拆票后**(有 explosive.fetch 那一笔)的单:拆票前的行没有 50 分取片费,合计比
 *   现价低 39~49,混进来会把下界拉到 47(60 天全量 n=30 时 P10/P50/P90 = 47/168/209)。
 *   拆票后:测试机 7 单(192 172 187 147 226 152 252)不足 20,演示机补 4 单
 *   (208 182 163,另 1 单解析失败只扣取片 50);演示机 09-19 之前的行是测试机租户移植的副本,
 *   不重复计。合计 n=11:P10=147 / P50=182 / P90=226(线性插值)→ 向外取整到 10 = 140~230。
 * ⚠️ 样本 n=11 仍薄(两台加起来也不到 20),攒够单子要回来重标。
 * ★2026-09-26 晚补样(修复波第八批 Z4,演示机实测 X 报告):T-6859651D 发现区拆 78 秒抖音片
 *   = 取片 50 + 解析 183 = **233**,破 230 上界 → 上界放到 240(n=12,仍是区间最长的一单 78s)。
 *   长片解析按实际用量,但有结算封顶(见下方 EXPLOSIVE_PARSE_SETTLE_CAP_CREDITS)。
 * ⚠️ **仅口播(audio_only)两台 60 天内 0 单** —— 没有实测就不编数,展示侧写「按实际用量」。
 */
/*
 * ★2026-10-05 补样(生产珠宝租户验收):一单 = 取片 50 + 解析 71 = **121**,低于旧下界 140 ——
 *   短片解析很便宜,140 起是高报。n=13 重算 P10=126.2 → 下界 120;P90=231.6 → 上界仍 240。
 */
export const EXPLOSIVE_FULL_JOB_CREDITS_RANGE: readonly [number, number] = [120, 240];

/** 完整拆解一单(取片+解析)的展示串。由上面的元组派生 —— 两处各写一份必然漂。 */
export const EXPLOSIVE_FULL_JOB_CREDITS_APPROX = `约 ${EXPLOSIVE_FULL_JOB_CREDITS_RANGE[0]}~${EXPLOSIVE_FULL_JOB_CREDITS_RANGE[1]}`;

/**
 * `explosive.parse` 的结算封顶(积分)—— api 第九批 Z9a 迁移 **V13** 把 settle_cap 从 NULL 写成 400。
 *
 * ★数字放常量、不散进句子:封顶再改时只动这一处;文案由它派生。
 * ★它是**单笔解析**的上限,不含取片 50(取片是另一张固定票)。
 */
export const EXPLOSIVE_PARSE_SETTLE_CAP_CREDITS = 400;

/**
 * 追爆「完整拆解」价签后面那一句。
 *
 * ★第八批 Z2 时 `explosive.parse` 的 settle_cap 还是 NULL,这句写的是「实际扣费可能更高」(无封顶披露);
 *   第九批 Z9a(api 迁移 V13)把封顶写成 400 之后,「可能更高」就成了对客少说一半 —— 用户该知道最多多少。
 * ★反向闸(creditRates.test):这句不许再出现「无封顶」「可能更高」而不带上限数字。
 */
/*
 * ★1005 生产实测:卡面写「最高 400」、确认框写「一单合计不超过 450」—— 两个数都对,但 400 只是
 *   **解析那一票**的封顶(api explosive.parse settle_cap=400),取片(explosive.fetch,LIST 50)是另一张
 *   固定票,真实一单上限 = 取片 + 400。卡面没有价目表取片价可用,所以明说「解析部分」「取片另计」,
 *   与确认框「解析最高 400、一单合计不超过 取片+400」同一口径。
 */
export const EXPLOSIVE_LONG_VIDEO_NOTE = `视频较长时解析按实际用量,解析部分最高 ${EXPLOSIVE_PARSE_SETTLE_CAP_CREDITS} 积分(取片另计)`;

/** 仅口播一单的实测区间:两台 0 单 → null(不编数)。攒到真单后按上面同一条 SQL 补标。 */
export const EXPLOSIVE_AUDIO_ONLY_JOB_CREDITS_RANGE: readonly [number, number] | null = null;

/**
 * 「拆一条」一单的价签(取片+解析合计),按档位给。
 * 仅口播没有实测区间 → 不出数字,只说怎么计。
 */
export function explosiveJobCreditsLabel(kind: "full" | "audio_only" | null | undefined): string {
  if (kind === "audio_only") {
    return EXPLOSIVE_AUDIO_ONLY_JOB_CREDITS_RANGE == null
      ? "取片+解析按实际用量计"
      : `取片+解析合计约 ${EXPLOSIVE_AUDIO_ONLY_JOB_CREDITS_RANGE[0]}~${EXPLOSIVE_AUDIO_ONLY_JOB_CREDITS_RANGE[1]} 积分`;
  }
  return `取片+解析合计${EXPLOSIVE_FULL_JOB_CREDITS_APPROX} 积分(${EXPLOSIVE_LONG_VIDEO_NOTE})`;
}

/**
 * 官媒舱(AI记者)积分标 —— **2026-08-27 新计费口径(×1.6)下重标**。
 *
 * ★旧值 15/20/4 是 0727 在 ×10 时代实测的(docs/2026-07-27-reporter-cost-measured.md),
 *   计费改 ×1.6 后一直没回来重标 = 对客虚报 3~5 倍。本次真机重测
 *   (T-55B5FD8C,deepseek-v4-flash,xb_billing_event 逐 settle 行):
 *   - 短文全链(整理+规划+生成):3 分(0+1+2,event 5401534530560/5401540297731)
 *     与 6 分(2+1+3,event 5401551045632/5401552618496/5401558647811)
 *   - 卡面 6 = 覆盖两样本高位;⚠️ **样本 n=2、单日**,攒单后回来重标(同 EXPLOSIVE 那条的规矩)
 * ★长文档无新实测:按 0727 长/短 token 比(17/14≈1.2)从 6 上浮取整 —— 不是实测锚,
 *   首笔长文真单落账后必须回来钉实测。整理档 0827 实测 0~2,取 2。
 */
export const REPORTER_CREDITS_PER_ARTICLE = 6;
export const REPORTER_CREDITS_LONGFORM = 8;
/** 整理档:0827 实测 0~2 取 2 → **0926 演示机 K5 实测 3**(1216 字素材),上界压住实测 → 3。 */
export const REPORTER_CREDITS_EXTRACT = 3;
/**
 * 1005 生产走查:整理按实际 token 用量结算,卡面「≈ 3」实扣 4(1 篇真单)。按量计费的东西不该报单点数 ——
 * 改报区间,上界压住已见实测最大值(4),下界取旧实测(2)。
 * ★1006 演示机最终回归:「约 2~4」实扣 5。计费口径 = `reporter.article` SKU 按 token MARKUP ×1.6、
 *   min_credits 1、封顶 50(api V1/V13),整理每跑一次结算一笔,素材越长越贵,没有固定档。
 *   1006 同一租户 4 轮整理实扣(积分明细「新闻稿」每轮第一笔):4 / 3 / 4 / 5(00:42 / 03:13 / 04:27 / 14:36),
 *   加 1005 生产 4、0926 K5 3 —— 6 笔全落在 3~5。上界压住实测最大值 5 再留 1 分余量(素材约 400 字,
 *   更长的素材会更高),下界取实测最小 3 → 「约 3~6」。0827 的 0~2 是更早模型/更短素材,不再作下界。
 */
export const REPORTER_CREDITS_EXTRACT_RANGE: readonly [number, number] = [3, 6];
export const REPORTER_CREDITS_EXTRACT_APPROX = `约 ${REPORTER_CREDITS_EXTRACT_RANGE[0]}~${REPORTER_CREDITS_EXTRACT_RANGE[1]}`;

/**
 * ★★2026-09-26 重标(修复波第十一批 B2 #5 ③):正文一篇报**区间**,不再单值。
 * 0827 两样本 3 / 6 是旧模型价;0926 演示机 K5 真跑 1216 字正文实扣 **10**(整理 3、规划 2 另计),
 * 卡面「≈ 6」低报近一半。样本 n=3 仍薄,口径沿用 EXPLOSIVE 那条:**上界压住实测最大值再留头**
 * (10 → 12),下界留 6(旧样本仍会出现)。长文(>2000 字)无实测,写「≈ 8 起」不编区间。
 */
export const REPORTER_ARTICLE_CREDITS_RANGE: readonly [number, number] = [6, 12];
export const REPORTER_ARTICLE_CREDITS_APPROX = `≈ ${REPORTER_ARTICLE_CREDITS_RANGE[0]}~${REPORTER_ARTICLE_CREDITS_RANGE[1]}`;

/** 「本篇预计 N 积分」的 N:短文区间 / 长文「≈ 8 起」。PlanPanel 与两处重写弹窗**同源**。 */
export function reporterArticleCreditsApprox(targetWords: number): string {
  return targetWords > 2000 ? `≈ ${REPORTER_CREDITS_LONGFORM} 起` : REPORTER_ARTICLE_CREDITS_APPROX;
}

/**
 * `reporter.article` 的结算封顶(积分)—— api 第九批 Z9a 迁移 **V13** 把 settle_cap 从 NULL 写成 50。
 * ★数字放常量,文案由它派生(同 EXPLOSIVE_PARSE_SETTLE_CAP_CREDITS 的规矩)。
 */
export const REPORTER_ARTICLE_SETTLE_CAP_CREDITS = 50;

/**
 * 官媒正文价签后面那一句。
 *
 * ★第八批 Z2 时 `reporter.article` 的 settle_cap 还是 NULL,这句写的是「可能更高」(无封顶披露);
 *   第九批 Z9a(api 迁移 V13)把封顶写成 50 之后,卡面 ≈N 旁边该说的是「最多到哪」,不是「不知道到哪」。
 * ★反向闸(creditRates.test / PlanPanel.mayExceed.test):不许再出现「无封顶」「可能更高」而不带上限数字。
 */
export const REPORTER_MAY_EXCEED_NOTE = `长文按实际用量,最高 ${REPORTER_ARTICLE_SETTLE_CAP_CREDITS} 积分`;

/**
 * 段落重写一次的实测区间(0926 K5:1~2 积分;B2 #5 ②)。此前弹窗写「整篇按重写计,≈6 积分」——
 * 重写只动一段,按 token 实扣通常 1~2,标 6 是高报 3~6 倍;封顶仍是正文那张票(50)。
 * 「≈ 6」与 PlanPanel「≈ 6~12」的空格形态由此统一(常量派生,不手抄)。
 */
export const REPORTER_REWRITE_CREDITS_RANGE: readonly [number, number] = [1, 2];
export const REPORTER_REWRITE_NOTE = `按实际用量计,通常 ${REPORTER_REWRITE_CREDITS_RANGE[0]}~${REPORTER_REWRITE_CREDITS_RANGE[1]} 积分(最高 ${REPORTER_ARTICLE_SETTLE_CAP_CREDITS} 积分)`;

// 声量雷达价签(原 GEO_RADAR_CREDITS_RANGE [30,50])已删:0831 终版定价把 geo.probe 改成
// LIST 固定价(888/次),价签自此走 /pricing/catalog(usePriceBook),这里不再留手抄快照 ——
// 0901 真机实锤:调价后这条常量原样对客展示「30~50 按用量」,而 settle 实扣 888。
/**
 * 自由创作「AI 润色」—— **现在不收费**。
 *
 * ★ 2026-08-26 批 L 核实:`ai.polish` 与 `poster.text_assist` 在 `xb_price_sku` 里
 *   都是 `pricing_mode = FREE`(老板拍板的 6 类免费动作之二),结算实扣 0。
 *   此前这里写「约 55~70 积分」—— 那是 2026-07-31 六次真跑的实测值,当时是对的;
 *   免费之后它就变成了**对着用户虚报一笔不存在的费用**,而且会劝退一个免费功能。
 * ★ 不留数字:留一个「0」也不对 —— 用户要看的是「这一步不花钱」,不是「这一步 0 积分」。
 */
export const POLISH_IS_FREE = true;
export const POLISH_COST_LABEL = "免费";

/**
 * 规划一次的实测区间:0827 两样本 1 / 1,0926 K5 实扣 2(「整理 3、规划 2 另计」)→ 1~2。
 */
export const REPORTER_PLAN_CREDITS_RANGE: readonly [number, number] = [1, 2];

/**
 * ★N4(1013):卡面「/ 篇」= **整篇**(整理 + 规划 + 正文三笔 `reporter.article` 结算之和),不是只算正文。
 * 此前卡面「≈ 6~12 / 篇」只是正文那一笔,用户走完一篇实扣约 15(0926 K5:整理 3 + 规划 2 + 正文 10)。
 * `reporter.article` 是按 token 计费(MARKUP)的 SKU,价目表 /pricing/catalog 只有单 token 单价、给不出「一篇多少」,
 * 所以整篇区间只能由三段实测区间**相加派生**(不另手抄一个数):下界 = 三段下界和,上界 = 三段上界和。
 * PlanPanel / 重写弹窗的「本篇预计」仍是正文那一笔(那时整理、规划已扣过)。
 */
export const REPORTER_WHOLE_ARTICLE_CREDITS_RANGE: readonly [number, number] = [
  REPORTER_CREDITS_EXTRACT_RANGE[0] +
    REPORTER_PLAN_CREDITS_RANGE[0] +
    REPORTER_ARTICLE_CREDITS_RANGE[0],
  REPORTER_CREDITS_EXTRACT_RANGE[1] +
    REPORTER_PLAN_CREDITS_RANGE[1] +
    REPORTER_ARTICLE_CREDITS_RANGE[1],
];
export const REPORTER_WHOLE_ARTICLE_CREDITS_APPROX = `≈ ${REPORTER_WHOLE_ARTICLE_CREDITS_RANGE[0]}~${REPORTER_WHOLE_ARTICLE_CREDITS_RANGE[1]}`;

/**
 * 「≈ 10~20 积分 / 篇」—— 官媒 6 张卡 + 部门卡的单源(N4:整篇口径,见 REPORTER_WHOLE_ARTICLE_CREDITS_RANGE)。
 * 批三口径:成稿实扣=LLM 成本派生(纯按实际消耗),数字是 1200 字档实测 —— 报区间不承诺定价。
 */
export const REPORTER_PER_ARTICLE_LABEL = `${REPORTER_WHOLE_ARTICLE_CREDITS_APPROX} 积分 / 篇`;

/**
 * 视频创建页的**落地默认档** —— 卡面报价与页内估算共用这一份,不许各写各的。
 *
 * ★A6(2026-08-01)修的就是「各写各的」:卡面写死 `15 秒`、报 8280,而创建页
 *   `useState<FormState>` 的 `durationSec` 是 **5**、且不预选时长档
 *   (`briefDuration` 缺省)→ `effectiveDurationSec` 返回 5 → 页内估算 **3280**。
 *   真跑实测(renderHook 跑真 `useVideoCreditEstimate`):卡面 8280 / 落地 3280,**差 2.52 倍**。
 *   用户在卡面看到的数字,点进去第一眼就对不上。
 *
 * ★口径对齐海报卡(`POSTER_CREDITS_PER_IMAGE(2K 默认档)`):**卡面报的是默认档**,
 *   不是随手举的一个配置。所以这两个常量是「创建页初始值」的真源,
 *   `VideoCreatePage` 的 initial form 反过来引用它们 —— 谁改默认,卡面自动跟着改。
 */
export const VIDEO_DEFAULT_CLIP_SECONDS = 5;
export const VIDEO_DEFAULT_RESOLUTION = "720p";

/**
 * 目的片(编排流)卡面报价用的默认时长档 = 「短 · 15s」(B2 #11 起目的流默认档;
 * 与 `effectiveDuration.DEFAULT_BRIEF_DURATION` → BRIEF_DURATION_SEC["短"]=15 同源)。
 */
export const VIDEO_PURPOSE_DEFAULT_CLIP_SECONDS = 15;

/**
 * 「AI 智能编排」一次的**结算封顶**(积分;B2 #9)。编排是 token 计费没有挂牌价、**没有下限**,
 * 只有 backend `xb_price_sku.settle_cap_credits=1900`(与 ai `VIDEO_EST_COMPOSE_HIGH` 同一个数)。
 * 卡面要在价目表之外静态报这个数(卡面不拉 `/video/compose/estimate`),故在此抄一份;创建页按钮旁
 * 读接口的 high,两处数字相同。
 * ★此前是 `VIDEO_COMPOSE_CREDITS_RANGE = [400, 1900]`(0926 K4 实测一次 1292 定的下沿),1006 新模型
 *   下实扣 235 / 123 都低于 400 —— 下沿改由 `VIDEO_COMPOSE_TYPICAL_RANGE`「通常」口径表达,不再写死成下限。
 * ★此前 8 张目的卡与「自由创作」卡共用一句「5 秒 720P 约 565(含 1 张台本图)」:直发路根本不出台本图
 *   (实扣 475 = video.render 一行),编排路又远不止 565 —— 两边都错。现在两条路各报各的。
 */
export const VIDEO_COMPOSE_CREDITS_CAP = 1900;

/**
 * 「AI 智能编排」一次的**常见区间**(对客口径:「按实际用量,通常 100~500,最高 1,900」)。
 *
 * ★1006 演示机最终回归:卡面「约 400~1900」,两次实扣 235 / 123 —— 都低于下限。根因不是计费出错:
 *   编排是 token 计费、只有封顶(`xb_price_sku.settle_cap_credits=1900`,ai `VIDEO_EST_COMPOSE_HIGH`)
 *   **没有下限**;而 api V9(eb148928,1006)把编排主力从 gpt-5.5($5/$30 每百万)换成 gpt-6.1-sol
 *   ($2/$10),单价降到 0.33~0.4 倍,400 这条下沿(0926 演示机 34 笔:最小约 400 / 中位 539 /
 *   p90 1190,全是 gpt-5.5 时代)整体失效。
 * ★区间怎么算的(不是拍的):0926 那 34 笔分布 × 新旧单价比(输出 1/3、输入 0.4):
 *   最小 400 → 133~160,中位 539 → 180~216,p90 1190 → 397~476;1006 真单 123 / 235 落在其中。
 *   下沿向下取整到 100、上沿向上取整到 500(覆盖 p90 高估端)。
 * ★上界(最高)不在这里写:仍读 `GET /video/compose/estimate` 的 high(= 结算硬顶同一个数),
 *   ai 侧 low(仍是 400)前端不再展示 —— 等 ai 侧随新模型重标后可回到单源。
 * ⚠️ 样本:新模型下只有 1006 两笔真单,攒到 20 笔以上回来按实测 p10/p90 重标。
 */
export const VIDEO_COMPOSE_TYPICAL_RANGE: readonly [number, number] = [100, 500];

/*
 * ★ `VIDEO_DEFAULT_CLIP_CREDITS` 已下线。它是 `5 秒 × 分辨率系数 × 每秒价 + 一张首帧` 算出来的,
 *   三个因子里有两个是已删的价格常量。默认档的**秒数与分辨率**留着(它们是创建页初始值的真源,
 *   `VideoCreatePage` 反过来引用它们),而**价钱由价目表现算** —— 见 catalog.ts 里那张卡。
 */

/** 「约 N 积分 / 秒」 */
export const perSecond = (credits: number) => `约 ${credits} 积分 / 秒`;

/** 「约 N 积分 / 张」 */
export const perImage = (credits: number) => `约 ${credits} 积分 / 张`;

/**
 * 千分位。四位数起卡面一眼读不准(`2100` 与 `21000` 差一个数量级却只差一个字符),
 * 而积分标恰恰是用户拿来做决定的那行数字。
 */
export const grouped = (credits: number) =>
  credits.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * 一屏最多装几段 —— 镜像 features 侧 `ONECLICK_SCREEN_MAX_SECTIONS`(ai `screens.py::SCREEN_MAX_SECTIONS`)。
 * entities 不能反向 import features,所以这里抄一份;`sceneCardLongImageParity.test.ts` 逐用途钉
 * `longImageCardScreens(n) === oneClickScreensOf(n)`,任一边改了必红。
 */
const LONGIMAGE_CARD_SCREEN_MAX_SECTIONS = 3;

/** 默认骨架块数 → 一键路**屏数下界**(最优装箱,每屏 3 段)。 */
export const longImageCardScreens = (blockCount: number) =>
  Math.max(1, Math.ceil(blockCount / LONGIMAGE_CARD_SCREEN_MAX_SECTIONS));

/**
 * 长图用途卡卡面标:「按屏计价 · 每屏 U 积分 · 默认约 S 屏 ≈ X 积分起」(1004 T3 统一措辞;此前「默认 N 块 · 按 S 屏计 · X 积分起」)(写「按…屏计」不写「约…屏」:搭建器里「约 N 屏」是手机滑屏长度,另一个量)。
 *
 * ★1003 B9:此前标「默认 N 块 · M 图块 ≈ 图块数×单价」—— 按**图块**算,而用途卡点进去落一键路,
 *   一键路**按屏**结算(LI-SCREEN-1 起每屏一次 gen,非图块有 DNA 也过引擎)。两者只在 goods 这种
 *   「图块数恰好 = ⌈块数/3⌉」的骨架上数值巧合,其余用途会低报。现在按屏:S = ⌈N/3⌉(屏数下界,
 *   与页内「一键出图」区间的下沿同一口径),X = S × 每屏单价,带「起」—— 密块最坏一块一屏,
 *   真实可能更多,低报是本仓 P0 病灶,所以不写「≈」。
 * ★X **算出来**,不许写字面量 —— 卡面数字与实扣差一个数量级是对客事故(E0 纠偏红线)。
 * 调用方给的 `blockCount` 由 `catalog.test.ts` 对着 `skeletonFor` 真骨架锁死。
 */
export const perScreens = (blockCount: number, perScreenCredits: number, tier?: string) => {
  const screens = longImageCardScreens(blockCount);
  // ★1005 生产验收:卡面「每屏 28」点进模板路落的是「标准 66/屏(2K)」—— 两个数都对,
  //   但卡面没说 28 是哪一档、也没说是最低档,用户读成「长图就是 28 一屏」。
  //   带档位时写「每屏 N 积分起(1K)」:N 是一键默认档(最低档)的价,更高档更贵。
  const unit = tier
    ? `按屏计价 · 每屏 ${grouped(perScreenCredits)} 积分起(${tier})`
    : perScreenPricing(perScreenCredits);
  return `${unit} · 默认约 ${screens} 屏 ≈ ${grouped(screens * perScreenCredits)} 积分起`;
};

/**
 * ★1004 T3:长图**唯一**的价格说法 ——「按屏计价 · 每屏 N 积分」。卡墙、搭建器路卡、一键按钮、
 *   备料卡脚部、失败重试那句全从这里取,不再各写一种(走查同一页看到三套:「按 3 屏计 · 84 起」
 *   /「约 28–308」/「1–3 屏 × 28 ≈ 28–84」)。N 由调用方从价目表取,这里只管措辞。
 */
export const perScreenUnitText = (perScreenCredits: number) =>
  `每屏 ${grouped(perScreenCredits)} 积分`;
export const perScreenPricing = (perScreenCredits: number) =>
  `按屏计价 · ${perScreenUnitText(perScreenCredits)}`;

/** 横宽卡面标:块数随版式变(1~3 区),所以只标图块数。 */
export const perImageBlocks = (imageBlocks: number, perImageCredits: number) =>
  `${imageBlocks} 图块 ≈ ${grouped(imageBlocks * perImageCredits)} 积分`;
